import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Printer, 
  Download, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldCheck, 
  RefreshCw, 
  Award,
  FileCheck
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { brandingService } from '../../../services/index.js';
import { getDefaultSchoolLogo, getDefaultQrCode } from '../certificates/CertificateRenderer';

const FeeClearanceCertificateModal = ({
  isOpen,
  onClose,
  student,
  fee,
  academicYear: propAcademicYear,
  orgData,
  brandingData,
  onCertificateIssued
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [isIssuing, setIsIssuing] = useState(false);
  const [issuedRecord, setIssuedRecord] = useState(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [selectedType, setSelectedType] = useState('FEE_CLEARANCE_CERTIFICATE'); // or 'NO_DUE_CERTIFICATE'
  const [signatoryRole, setSignatoryRole] = useState('Principal');

  const certificateRef = useRef(null);

  // Active academic year (from props, or fee, or 2026-27 fallback)
  const academicYear = propAcademicYear || fee?.academicYear || student?.admissionYear || '2026-27';

  // Load authoritative fee clearance status from backend
  const checkClearanceStatus = async () => {
    if (!student) return;
    try {
      setLoading(true);
      setError(null);
      const studentIdKey = student.studentId || student.admissionNo || student.id;
      const res = await brandingService.checkFeeClearance(studentIdKey, academicYear, fee?.feeId);
      setEligibilityData(res);
      if (res?.existingCertificate) {
        setIssuedRecord(res.existingCertificate);
      }
    } catch (err) {
      console.error('Failed to verify fee clearance:', err);
      setError(err?.message || 'Unable to verify fee clearance from server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && student) {
      checkClearanceStatus();
    } else {
      setEligibilityData(null);
      setIssuedRecord(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, student, academicYear, fee?.feeId]);

  if (!isOpen || !student) return null;

  // School Branding details
  const schoolName = brandingData?.schoolName || orgData?.institutionName || orgData?.orgName || orgData?.name || 'School';
  const schoolAddress = brandingData?.schoolAddress || orgData?.address || '';
  const schoolPhone = brandingData?.schoolPhone || orgData?.phoneNumber || orgData?.contactNumber || orgData?.phone || '';
  const schoolEmail = brandingData?.schoolEmail || orgData?.email || '';
  const schoolAffiliation = brandingData?.schoolRegistrationNo || orgData?.affiliation || orgData?.registrationNumber || '';
  const schoolUdise = brandingData?.udiseCode || orgData?.udiseCode || '';
  const logoUrl = brandingData?.logoUrl || orgData?.logo || getDefaultSchoolLogo('#1e3a8a', schoolName);
  const principalSignatureUrl = brandingData?.principalSignatureUrl || brandingData?.signatureUrl || null;
  const sealUrl = brandingData?.sealUrl || null;
  const principalName = brandingData?.principalName || orgData?.principalName || 'Principal';

  // Student Particulars
  const studentName = eligibilityData?.student?.name || student?.name || 'Student';
  const admissionNo = eligibilityData?.student?.admissionNo || student?.admissionNo || student?.studentId || 'N/A';
  const rollNo = eligibilityData?.student?.rollNo || student?.rollNo || student?.rollNumber || 'N/A';
  const className = eligibilityData?.student?.className || student?.className || student?.class || 'N/A';
  const section = eligibilityData?.student?.section || student?.section || 'A';
  const fatherName = eligibilityData?.student?.fatherName || student?.fatherName || student?.guardianName || student?.motherName || 'Parent/Guardian';

  // Financial status from backend with robust fallback from row props so amount is never 0 when liability exists
  const serverAssessed = eligibilityData?.totalAssessed != null ? Number(eligibilityData.totalAssessed) : 0;
  const serverPaid = eligibilityData?.paidAmount != null ? Number(eligibilityData.paidAmount) : 0;
  const serverPending = eligibilityData?.pendingFee != null ? Number(eligibilityData.pendingFee) : 0;

  const propAssessed = fee?.totalFees != null ? Number(fee.totalFees) : (fee?.amount != null ? Number(fee.amount) : 0);
  const propPaid = fee?.paidFees != null ? Number(fee.paidFees) : (fee?.paidAmount != null ? Number(fee.paidAmount) : 0);
  const propPending = fee?.pendingFee != null ? Number(fee.pendingFee) : (fee?.pendingAmount != null ? Number(fee.pendingAmount) : (fee?.dueAmount != null ? Number(fee.dueAmount) : 0));

  const totalAssessed = serverAssessed > 0 ? serverAssessed : (propAssessed > 0 ? propAssessed : serverAssessed);
  const paidAmount = serverAssessed > 0 ? serverPaid : (propAssessed > 0 ? propPaid : serverPaid);
  const pendingFee = serverAssessed > 0 ? serverPending : (propAssessed > 0 ? propPending : serverPending);

  const isEligible = (eligibilityData?.eligible === true) || (totalAssessed > 0 && pendingFee <= 0);

  // Certificate Number
  const certNumber = issuedRecord?.certificateNumber || (isEligible ? `FCC/${academicYear}/PREVIEW` : 'NOT_ELIGIBLE');
  const issueDateFormatted = issuedRecord?.issuedDate 
    ? new Date(issuedRecord.issuedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  // Handler: Issue Official Certificate
  const handleIssueCertificate = async () => {
    if (!isEligible) return;
    try {
      setIsIssuing(true);
      setError(null);
      const payload = {
        studentId: student.id || student.studentId || student.admissionNo,
        feeId: fee?.feeId,
        certificateType: selectedType,
        certificateCategory: 'FEES',
        templateName: 'OFFICIAL_FEE_CLEARANCE',
        pageSize: 'A4',
        orientation: 'PORTRAIT',
        academicYear: academicYear,
        issuedBy: signatoryRole,
        reissue: !!issuedRecord // Allow explicit reissue if already issued
      };

      const res = await brandingService.issueCertificate(payload);
      if (res && res.certificateNumber) {
        setIssuedRecord(res);
        if (onCertificateIssued) {
          onCertificateIssued(res);
        }
      }
    } catch (err) {
      console.error('Failed to issue certificate:', err);
      setError(err?.message || 'Failed to issue certificate. Please try again.');
    } finally {
      setIsIssuing(false);
    }
  };

  // Handler: Print Certificate
  const handlePrint = () => {
    window.print();
  };

  // Handler: Download PDF
  const handleDownloadPdf = async () => {
    if (!certificateRef.current) return;
    try {
      setIsDownloadingPdf(true);
      const element = certificateRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, Math.min(pdfHeight, 297));
      const cleanFileName = `Fee_Clearance_Certificate_${admissionNo.replace(/[^a-zA-Z0-9_-]/g, '_')}_${academicYear}.pdf`;
      pdf.save(cleanFileName);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Unable to generate PDF. You can also use the Print button to Save as PDF.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div
      className="fee-clearance-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(5px)',
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      {/* Global CSS for Print-only A4 formatting */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          .fee-clearance-printable, .fee-clearance-printable * {
            visibility: visible !important;
          }
          .fee-clearance-modal-overlay {
            position: absolute !important;
            inset: 0 !important;
            background: transparent !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          .fee-clearance-modal-wrapper {
            box-shadow: none !important;
            border: none !important;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .fee-clearance-modal-header,
          .fee-clearance-toolbar,
          .fee-clearance-status-banner {
            display: none !important;
          }
          .fee-clearance-printable {
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100vw !important;
            min-height: 100vh !important;
            margin: 0 !important;
            padding: 24mm 20mm !important;
            border: 2px solid #1e3a8a !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>

      <div
        className="fee-clearance-modal-wrapper"
        style={{
          width: '100%',
          maxWidth: '850px',
          maxHeight: '92vh',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1061
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          className="fee-clearance-modal-header"
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Award size={20} color="#f59e0b" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', letterSpacing: '-0.2px' }}>
                Fee Clearance / No Dues Certificate
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                Official Financial Statement • Academic Year: <span style={{ color: '#fcd34d', fontWeight: '600' }}>{academicYear}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body / Scrollable Content */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, background: '#f8fafc' }}>
          
          {/* 1. Loading State */}
          {loading && (
            <div style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
              <RefreshCw size={28} className="spin-animate" style={{ marginBottom: '12px', color: '#3b82f6' }} />
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>Checking Authoritative Fee Clearance...</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>Verifying ledger records against organization and academic year</div>
            </div>
          )}

          {/* 2. Error / Warning Safeguard Banner */}
          {!loading && error && (
            <div
              className="fee-clearance-status-banner"
              style={{
                marginBottom: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}
            >
              <AlertTriangle size={18} color="#dc2626" style={{ marginTop: '2px', flexShrink: 0 }} />
              <div style={{ fontSize: '13px', color: '#991b1b', lineHeight: 1.4 }}>
                <span style={{ fontWeight: '700' }}>Verification Alert: </span>
                {error}
              </div>
            </div>
          )}

          {/* 3. Ineligibility Notification (Zero Fee or Pending Dues) */}
          {!loading && eligibilityData && !isEligible && (
            <div
              className="fee-clearance-status-banner"
              style={{
                marginBottom: '16px',
                padding: '14px 18px',
                borderRadius: '10px',
                backgroundColor: pendingFee > 0 ? '#fffbeb' : '#f8fafc',
                border: `1.5px solid ${pendingFee > 0 ? '#fde68a' : '#cbd5e1'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle size={20} color={pendingFee > 0 ? '#d97706' : '#64748b'} />
                <div>
                  <div style={{ fontWeight: '700', fontSize: '13.5px', color: pendingFee > 0 ? '#92400e' : '#334155' }}>
                    {pendingFee > 0 
                      ? `Fee Clearance Certificate cannot be issued because this student has an outstanding balance of ₹${pendingFee.toLocaleString('en-IN')}.`
                      : (eligibilityData.reason || 'No fee assessment found for this academic year.')
                    }
                  </div>
                  <div style={{ fontSize: '12px', color: pendingFee > 0 ? '#b45309' : '#64748b', marginTop: '2px' }}>
                    Total Assessed: ₹{totalAssessed.toLocaleString('en-IN')} • Total Paid: ₹{paidAmount.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={checkClearanceStatus}
                style={{ fontSize: '11px', fontWeight: '600', border: '1px solid #cbd5e1' }}
              >
                <RefreshCw size={12} /> Re-check
              </button>
            </div>
          )}

          {/* 4. Eligible & Clearance Confirmed Banner */}
          {!loading && isEligible && (
            <div
              className="fee-clearance-status-banner"
              style={{
                marginBottom: '16px',
                padding: '12px 18px',
                borderRadius: '10px',
                backgroundColor: '#f0fdf4',
                border: '1.5px solid #bbf7d0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle2 size={20} color="#16a34a" />
                <div>
                  <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#166534' }}>
                    Authoritative Backend Clearance Verified • Nil Balance
                  </div>
                  <div style={{ fontSize: '12px', color: '#15803d', marginTop: '2px' }}>
                    Total Assessed: <strong>₹{totalAssessed.toLocaleString('en-IN')}</strong> • Total Paid: <strong>₹{paidAmount.toLocaleString('en-IN')}</strong> • Outstanding: <strong>₹0.00</strong>
                  </div>
                </div>
              </div>

              {issuedRecord ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', background: '#dcfce7', padding: '4px 10px', borderRadius: '6px', color: '#14532d', fontWeight: '700' }}>
                  <FileCheck size={14} /> Officially Issued: {issuedRecord.certificateNumber}
                </div>
              ) : (
                <span style={{ fontSize: '12px', color: '#65a30d', fontWeight: '600' }}>
                  Ready to Issue
                </span>
              )}
            </div>
          )}

          {/* 5. Customization Controls for Admin (Type & Signatory) */}
          {!loading && isEligible && (
            <div
              className="fee-clearance-toolbar"
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569', margin: 0 }}>
                    Certificate Type:
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    style={{ fontSize: '12px', fontWeight: '600', padding: '4px 8px', borderRadius: '6px' }}
                  >
                    <option value="FEE_CLEARANCE_CERTIFICATE">Fee Clearance Certificate</option>
                    <option value="NO_DUE_CERTIFICATE">No Dues Certificate</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569', margin: 0 }}>
                    Signatory:
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={signatoryRole}
                    onChange={(e) => setSignatoryRole(e.target.value)}
                    placeholder="e.g. Principal"
                    style={{ width: '130px', fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                  />
                </div>
              </div>

              <div>
                {!issuedRecord ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={handleIssueCertificate}
                    disabled={isIssuing}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '12px',
                      fontWeight: '700',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)'
                    }}
                  >
                    {isIssuing ? <RefreshCw size={13} className="spin-animate" /> : <ShieldCheck size={14} />}
                    {isIssuing ? 'Issuing...' : '✓ Issue Certificate'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleIssueCertificate}
                    disabled={isIssuing}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '4px 10px',
                      borderRadius: '6px'
                    }}
                    title="Generate a new official revision or reissue"
                  >
                    {isIssuing ? <RefreshCw size={12} className="spin-animate" /> : <RefreshCw size={12} />}
                    Re-issue Revision
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 6. A4 Realistic Portrait Preview */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              ref={certificateRef}
              className="fee-clearance-printable"
              style={{
                width: '100%',
                maxWidth: '720px',
                backgroundColor: '#ffffff',
                border: '3px double #1e3a8a',
                borderRadius: '8px',
                padding: '36px 40px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                color: '#0f172a',
                fontFamily: "'Times New Roman', Times, serif",
                position: 'relative',
                boxSizing: 'border-box'
              }}
            >
              {/* Inner Ornamental Border */}
              <div
                style={{
                  border: '1px solid #d97706',
                  padding: '24px 28px',
                  borderRadius: '4px',
                  position: 'relative'
                }}
              >
                {/* Header with School Branding */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1e3a8a', paddingBottom: '16px', marginBottom: '20px' }}>
                  <div style={{ width: '80px', height: '80px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={logoUrl}
                      alt="School Logo"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={(e) => { e.currentTarget.src = getDefaultSchoolLogo('#1e3a8a', schoolName); }}
                    />
                  </div>

                  <div style={{ textAlign: 'center', flex: 1, padding: '0 16px' }}>
                    <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {schoolName}
                    </h1>
                    {schoolAddress && (
                      <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#475569' }}>
                        {schoolAddress}
                      </p>
                    )}
                    <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      {schoolPhone && <span>Tel: {schoolPhone}</span>}
                      {schoolEmail && <span>Email: {schoolEmail}</span>}
                      {schoolAffiliation && <span>Affiliation / Code: {schoolAffiliation}</span>}
                      {schoolUdise && <span>UDISE: {schoolUdise}</span>}
                    </div>
                  </div>

                  <div style={{ width: '80px', height: '80px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {sealUrl ? (
                      <img src={sealUrl} alt="Seal" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ width: '68px', height: '68px', border: '1.5px dashed #94a3b8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#94a3b8', textAlign: 'center', fontWeight: '600' }}>
                        INSTITUTION<br/>SEAL
                      </div>
                    )}
                  </div>
                </div>

                {/* Certificate Title */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#1e3a8a',
                      color: '#ffffff',
                      padding: '4px 18px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      marginBottom: '6px'
                    }}
                  >
                    Official Document
                  </span>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '800',
                      color: '#92400e',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}
                  >
                    {selectedType === 'NO_DUE_CERTIFICATE' ? 'NO DUES CERTIFICATE' : 'FEE CLEARANCE CERTIFICATE'}
                  </h2>
                </div>

                {/* Metadata Row: Certificate No & Date */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '11.5px',
                    fontWeight: '600',
                    color: '#334155',
                    borderBottom: '1px dashed #cbd5e1',
                    paddingBottom: '8px',
                    marginBottom: '18px'
                  }}
                >
                  <div>
                    Certificate No: <span style={{ fontFamily: 'monospace', color: '#0f172a', fontWeight: '700' }}>{certNumber}</span>
                  </div>
                  <div>
                    Date of Issue: <span style={{ color: '#0f172a' }}>{issueDateFormatted}</span>
                  </div>
                </div>

                {/* Body Statement */}
                <div style={{ fontSize: '13.5px', lineHeight: 1.7, textAlign: 'justify', color: '#1e293b', marginBottom: '20px' }}>
                  This is to officially certify that Master / Miss <strong>{studentName}</strong>, 
                  Son/Daughter of <strong>{fatherName}</strong>, bearing Admission No. <strong>{admissionNo}</strong>, 
                  Roll No. <strong>{rollNo}</strong>, enrolled in Class <strong>{className}</strong> (Section <strong>{section}</strong>), 
                  has cleared all assessed school fees and financial obligations for the academic year <strong>{academicYear}</strong>.
                </div>

                {/* Financial Clearance Snapshot Table */}
                <div style={{ marginBottom: '22px' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '12px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #cbd5e1' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '700', color: '#334155' }}>Particulars</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '700', color: '#334155' }}>Academic Year</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: '#334155' }}>Amount (INR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '7px 12px', color: '#475569' }}>Total Assessed School Fees</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', color: '#475569' }}>{academicYear}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: '600' }}>₹{totalAssessed.toLocaleString('en-IN')}.00</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '7px 12px', color: '#475569' }}>Total Fees Received & Credited</td>
                        <td style={{ padding: '7px 12px', textAlign: 'center', color: '#475569' }}>{academicYear}</td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: '600', color: '#15803d' }}>₹{paidAmount.toLocaleString('en-IN')}.00</td>
                      </tr>
                      <tr style={{ backgroundColor: '#fefce8', fontWeight: '800' }}>
                        <td style={{ padding: '9px 12px', color: '#854d0e' }}>Net Outstanding Fee Balance</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', color: '#854d0e' }}>NIL / FULLY CLEARED</td>
                        <td style={{ padding: '9px 12px', textAlign: 'right', color: '#166534', fontSize: '13px' }}>₹0.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Formal Statement */}
                <div
                  style={{
                    backgroundColor: '#f8fafc',
                    borderLeft: '4px solid #1e3a8a',
                    padding: '10px 14px',
                    fontSize: '11.5px',
                    fontStyle: 'italic',
                    color: '#334155',
                    marginBottom: '36px',
                    lineHeight: 1.5
                  }}
                >
                  "This certificate confirms that no outstanding school fee balance is recorded for the above student for the specified academic year as of the date of issue."
                </div>

                {/* Signatures and QR Code Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '20px', paddingTop: '10px' }}>
                  {/* Candidate / Parent Signature */}
                  <div style={{ textAlign: 'center', width: '150px' }}>
                    <div style={{ height: '40px' }}></div>
                    <div style={{ borderTop: '1px solid #64748b', paddingTop: '4px', fontSize: '11px', fontWeight: '700', color: '#334155' }}>
                      Parent / Guardian
                    </div>
                  </div>

                  {/* QR Code Verification */}
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        padding: '4px',
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}
                    >
                      <img
                        src={getDefaultQrCode(certNumber)}
                        alt="QR Verification"
                        style={{ width: '60px', height: '60px' }}
                      />
                    </div>
                    <div style={{ fontSize: '9px', color: '#64748b', marginTop: '3px', fontWeight: '600' }}>
                      Certificate Verification
                    </div>
                  </div>

                  {/* Class Teacher */}
                  <div style={{ textAlign: 'center', width: '150px' }}>
                    <div style={{ height: '40px' }}></div>
                    <div style={{ borderTop: '1px solid #64748b', paddingTop: '4px', fontSize: '11px', fontWeight: '700', color: '#334155' }}>
                      Class Teacher
                    </div>
                  </div>

                  {/* Principal / Head of Institution */}
                  <div style={{ textAlign: 'center', width: '160px' }}>
                    <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {principalSignatureUrl && (
                        <img src={principalSignatureUrl} alt="Signature" style={{ maxHeight: '38px', maxWidth: '120px', objectFit: 'contain' }} />
                      )}
                    </div>
                    <div style={{ borderTop: '1px solid #64748b', paddingTop: '4px', fontSize: '11px', fontWeight: '700', color: '#334155' }}>
                      {principalName}<br/>
                      <span style={{ fontSize: '9.5px', fontWeight: '600', color: '#64748b' }}>{signatoryRole}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer / Action Buttons */}
        <div
          className="fee-clearance-toolbar"
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            {isEligible ? (
              <span>✓ Verified against active fee ledger • Single-page A4 print format</span>
            ) : (
              <span style={{ color: '#dc2626' }}>⚠️ Settlement of pending fee is required before issuance</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onClose}
              style={{ fontSize: '12px', fontWeight: '600', padding: '6px 14px' }}
            >
              Close
            </button>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handlePrint}
              disabled={!isEligible}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '12px',
                fontWeight: '600',
                padding: '6px 14px'
              }}
              title="Print Certificate (A4 Portrait)"
            >
              <Printer size={14} /> Print Certificate
            </button>

            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleDownloadPdf}
              disabled={!isEligible || isDownloadingPdf}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '12px',
                fontWeight: '700',
                padding: '6px 16px',
                backgroundColor: '#1e3a8a',
                borderColor: '#1e3a8a'
              }}
              title="Download High-Resolution PDF"
            >
              {isDownloadingPdf ? <RefreshCw size={14} className="spin-animate" /> : <Download size={14} />}
              {isDownloadingPdf ? 'Generating PDF...' : '📥 Download PDF'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FeeClearanceCertificateModal;
